/**
 * Modal to add/save a payment method (card/UPI/bank) via Stripe SetupIntent.
 * clientSecret must come from the SetupIntent API (POST /payments/stripe/setup-intent);
 * it is a one-time-use secret so it is never hardcoded. Shows loader until it is available.
 *
 * Tabbed UI (Card / Bank account / UPI) is controlled by the backend when creating
 * the SetupIntent: pass payment_method_types: ['card', 'us_bank_account', 'upi']
 * so Stripe shows all options. The client_secret encodes which methods are allowed;
 * the frontend cannot enable extra methods.
 */
import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import { Elements } from '@stripe/react-stripe-js';
import { useLayoutEffect, useMemo, useRef, useState } from 'react';

import { app_config } from '../../api/app-config';
import { useStripeInstance } from '../../hooks/useStripeInstance';
import { useTranslation } from '../../utils/translations';
import CustomButton from '../button/ButtonComponent';
import { CommonModal } from '../modal/modal';
import { colors } from '../theme/theme';

import PaymentMethodForm from './AddPaymentMethodForm';

export interface SavedPaymentMethodDisplay {
    id: string;
    label: string;
    last4?: string;
    type?: 'card' | 'us_bank_account';
    brand?: string;
    expiryMonth?: number;
    expiryYear?: number;
    bankName?: string;
    accountHolderName?: string;
    accountType?: string;
    country?: string;
    funding?: string;
}

interface AddPaymentMethodModalProps {
    open: boolean;
    onClose: () => void;
    onSuccess?: (paymentMethod: SavedPaymentMethodDisplay) => void;
    /** SetupIntent client secret from API (POST /payments/stripe/setup-intent). When provided, used instead of env/static fallback. */
    clientSecret?: string | null;
}

const appearance = {
    theme: 'stripe' as const,
    variables: {
        colorPrimary: colors.primaryLight?.[100] || '#0066cc',
        borderRadius: '8px',
    },
};

export function AddPaymentMethodModal({
    open,
    onClose,
    onSuccess,
    clientSecret: clientSecretProp,
}: AddPaymentMethodModalProps) {
    const { t } = useTranslation();
    const stripePromise = useStripeInstance();
    const clientSecret = clientSecretProp?.trim() || app_config.stripeSetupIntentClientSecret?.trim() || '';
    const [elementsKey, setElementsKey] = useState(0);
    const prevOpenRef = useRef(false);

    // Remount Elements each time the modal opens so we always have a fresh Payment Element
    // and avoid "elements should have a mounted Payment Element" from stale/consumed state.
    useLayoutEffect(() => {
        if (open && !prevOpenRef.current) {
            console.log(
                '[AddPaymentMethodModal] opened with clientSecret:',
                clientSecret ? `${clientSecret.slice(0, 25)}...` : '(none)',
            );
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setElementsKey(k => k + 1);
        }
        prevOpenRef.current = open;
    }, [open, clientSecret]);

    const options = useMemo(
        () => ({
            clientSecret,
            appearance,
        }),
        [clientSecret],
    );

    const handleSuccess = (paymentMethod: SavedPaymentMethodDisplay) => {
        onSuccess?.(paymentMethod);
        onClose();
    };

    return (
        <CommonModal
            modalOpen={open}
            onCloseModal={onClose}
            DialogMainTitle={t('addPaymentMethodModal.title')}
            DialogSubTitle={t('addPaymentMethodModal.subTitle')}
            ModalMaxWidth={550}
            showButtons={false}
        >
            <Box sx={{ pt: 0, pb: 2 }}>
                {open && !clientSecretProp?.trim() && !clientSecret ? (
                    <Stack alignItems="center" spacing={2} py={4}>
                        <CircularProgress size={32} />
                        <Typography color="textSecondary">{t('addPaymentMethodModal.loadingPaymentForm')}</Typography>
                    </Stack>
                ) : clientSecret ? (
                    <Elements
                        key={`add-payment-${elementsKey}-${clientSecret}`}
                        stripe={stripePromise}
                        options={options}
                    >
                        <PaymentMethodForm onSuccess={handleSuccess} onCancel={onClose} />
                    </Elements>
                ) : (
                    <Stack alignItems="center" spacing={2} py={3}>
                        <Typography color="textSecondary">{t('addPaymentMethodModal.stripeNotConfigured')}</Typography>
                        <CustomButton variant="outlined" onClick={onClose}>
                            {t('addPaymentMethodModal.close')}
                        </CustomButton>
                    </Stack>
                )}
            </Box>
        </CommonModal>
    );
}

export default AddPaymentMethodModal;
